import * as React from 'react'

import { PanelLeftClose, PanelLeftOpen, Pin } from 'lucide-react'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import SetUpNavigationRow from '~/client/src/desktop/views/ProjectSetUp/components/SetUpSteps/SetUpNavigationRow'
import ProjectSetUpPageStore from '~/client/src/desktop/views/ProjectSetUp/ProjectSetUpPage.store'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/client/src/shared/components/shadcn-ui/tooltip'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import ClientInfo from './ClientInfo'

// The header widens over `$sidebar-animation-time` (0.2s) with its content
// right-aligned, so the pin button slides under a cursor still resting on
// "Expand menu". Presses that land before the animation settles are a fast
// second click, not a choice to unpin (which hides the sidebar outside Settings).
export const PIN_LOCK_AFTER_EXPAND_MS = 300

// The sidebar is dark in both themes, so its icon buttons use a white overlay
// instead of the ghost variant's theme-dependent muted fill.
const ICON_BUTTON_CLASS = 'tw:hover:bg-white/8 tw:dark:hover:bg-white/8'
const PRESSED_ICON_BUTTON_CLASS =
  'tw:bg-white/16 tw:hover:bg-white/16 tw:dark:hover:bg-white/16'
const ICON_CLASS = 'tw:size-4.5 tw:text-white'

interface ISetUpNavigationProps {
  projectSetUpPageStore: ProjectSetUpPageStore
  className?: string
  sidebarOnly?: boolean

  eventsStore?: DesktopEventStore
}

@inject('eventsStore')
@observer
export default class SetUpNavigation extends React.Component<ISetUpNavigationProps> {
  private expandedAt = 0

  private toggleSetUpPagesPanel = () => {
    const {
      projectSetUpPageStore: {
        setSidebarCollapsed,
        isSettingsSidebarCollapsed,
        isSharedSidebarCollapsed,
      },
      sidebarOnly,
    } = this.props

    if (this.isSidebarCollapsed) {
      this.expandedAt = Date.now()
    }

    // Toggle the correct sidebar state
    if (sidebarOnly) {
      setSidebarCollapsed(!isSharedSidebarCollapsed)
    } else {
      setSidebarCollapsed(!isSettingsSidebarCollapsed)
    }
  }

  private togglePinned = () => {
    if (Date.now() - this.expandedAt < PIN_LOCK_AFTER_EXPAND_MS) {
      return
    }

    this.props.projectSetUpPageStore.toggleShowSidebarOutsideOfSettings()
  }

  public render() {
    const store = this.props.projectSetUpPageStore
    const {
      displayAccountSettingsPages,
      projectSettingsPages,
      locationSettingsPages,
      formsSettingsPages,
      resourcesSettingsPages,
      legacySettingsPages,
      moreSettingsPages,
      integrationSettingsPages,
      isAdmin,
      isIntegrationsEnabled,
      isFormsDisabled,
    } = store
    const isSidebarCollapsed = this.isSidebarCollapsed

    return (
      <div
        className={classList({
          'project-setup-leftbar-holder col': true,
          [this.props.className]: !!this.props.className,
        })}
      >
        <div
          className={classList({
            'project-setup-leftbar-header pb0 menu-toggle-icon-holder row no-grow navigation-group': true,
            short: isSidebarCollapsed,
            'pa10 x-end': !isSidebarCollapsed,
          })}
        >
          {!isSidebarCollapsed && (
            <div className="text white bold large flex-grow overflow-hidden pl6">
              {Localization.translator.settings}
            </div>
          )}
          <div
            className={classList({
              'tw:flex tw:h-11 tw:items-center tw:gap-1 no-grow': true,
              'tw:pl-3': isSidebarCollapsed,
            })}
          >
            {!isSidebarCollapsed && this.isAdmin && this.renderPinButton()}
            {this.renderCollapseButton()}
          </div>
        </div>
        <div
          id="project-setup-leftbar-scrollable"
          className={classList({
            'project-setup-leftbar full-height col scrollable': true,
            short: isSidebarCollapsed,
          })}
        >
          {this.renderSeparator()}
          <div className="navigation-group">
            <div
              className={classList({
                row: true,
                'x-end': !isSidebarCollapsed,
                'x-center': isSidebarCollapsed,
              })}
            ></div>
            <div className="navigation-group-header pb5 title">
              {Localization.translator.account}
            </div>
            {displayAccountSettingsPages.map(step => (
              <SetUpNavigationRow
                key={store.getKeyForStep(step)}
                step={step}
                projectSetUpPageStore={store}
                isShortVersion={isSidebarCollapsed}
              />
            ))}
          </div>
          {isAdmin && (
            <>
              <div className="navigation-group">
                {this.renderTitle(Localization.translator.project)}
                {projectSettingsPages.map(step => (
                  <SetUpNavigationRow
                    key={store.getKeyForStep(step)}
                    step={step}
                    projectSetUpPageStore={store}
                    isShortVersion={isSidebarCollapsed}
                  />
                ))}
                {isIntegrationsEnabled &&
                  integrationSettingsPages.map(step => (
                    <SetUpNavigationRow
                      key={store.getKeyForStep(step)}
                      step={step}
                      projectSetUpPageStore={store}
                      isShortVersion={isSidebarCollapsed}
                    />
                  ))}
              </div>
              <div className="navigation-group">
                {this.renderTitle(Localization.translator.locations)}
                {locationSettingsPages.map(step => (
                  <SetUpNavigationRow
                    key={store.getKeyForStep(step)}
                    step={step}
                    projectSetUpPageStore={store}
                    isShortVersion={isSidebarCollapsed}
                  />
                ))}
              </div>
              <div className="navigation-group">
                {this.renderTitle(Localization.translator.resources)}
                {resourcesSettingsPages.map(step => (
                  <SetUpNavigationRow
                    key={store.getKeyForStep(step)}
                    step={step}
                    projectSetUpPageStore={store}
                    isShortVersion={isSidebarCollapsed}
                  />
                ))}
              </div>
              {!isFormsDisabled && (
                <div className="navigation-group">
                  {this.renderTitle(Localization.translator.workflows)}
                  {formsSettingsPages.map(step => (
                    <SetUpNavigationRow
                      key={store.getKeyForStep(step)}
                      step={step}
                      projectSetUpPageStore={store}
                      isShortVersion={isSidebarCollapsed}
                    />
                  ))}
                </div>
              )}
              {legacySettingsPages.length > 0 && (
                <div className="navigation-group">
                  {this.renderTitle(Localization.translator.legacy)}
                  {legacySettingsPages.map(step => (
                    <SetUpNavigationRow
                      key={store.getKeyForStep(step)}
                      step={step}
                      projectSetUpPageStore={store}
                      isShortVersion={isSidebarCollapsed}
                    />
                  ))}
                </div>
              )}

              {moreSettingsPages.length > 0 && (
                <div className="navigation-group">
                  {this.renderTitle(Localization.translator.more)}
                  {moreSettingsPages.map(step => (
                    <SetUpNavigationRow
                      key={store.getKeyForStep(step)}
                      step={step}
                      projectSetUpPageStore={store}
                      isShortVersion={isSidebarCollapsed}
                    />
                  ))}
                </div>
              )}
            </>
          )}
          <div className="col y-end no-select client-info">
            <ClientInfo shouldShow={true} />
          </div>
        </div>
      </div>
    )
  }

  private renderPinButton(): JSX.Element {
    const isPinned =
      this.props.projectSetUpPageStore.showSidebarOutsideOfSettings
    const label = isPinned
      ? Localization.translator.unpinSettingsSidebar
      : Localization.translator.pinSettingsSidebar

    return (
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={this.togglePinned}
              aria-label={label}
              aria-pressed={isPinned}
              className={
                isPinned ? PRESSED_ICON_BUTTON_CLASS : ICON_BUTTON_CLASS
              }
              data-testid="setup-sidebar-pin-toggle"
            />
          }
        >
          <Pin className={ICON_CLASS} />
        </TooltipTrigger>
        <TooltipContent side="bottom">{label}</TooltipContent>
      </Tooltip>
    )
  }

  private renderCollapseButton(): JSX.Element {
    const isSidebarCollapsed = this.isSidebarCollapsed
    const label = isSidebarCollapsed
      ? Localization.translator.expandMenu
      : Localization.translator.collapseMenu

    return (
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={this.toggleSetUpPagesPanel}
              aria-label={label}
              className={ICON_BUTTON_CLASS}
              data-testid="setup-sidebar-toggle-button"
            />
          }
        >
          {isSidebarCollapsed ? (
            <PanelLeftOpen className={ICON_CLASS} />
          ) : (
            <PanelLeftClose className={ICON_CLASS} />
          )}
        </TooltipTrigger>
        {/* Clears the sidebar's right edge in both widths */}
        <TooltipContent side="right" sideOffset={isSidebarCollapsed ? 29 : 28}>
          {label}
        </TooltipContent>
      </Tooltip>
    )
  }

  private renderTitle(
    title: string,
    className: string = 'navigation-group-header title row x-between',
  ) {
    return (
      <>
        {this.renderSeparator()}
        <div className={className}>{title}</div>
      </>
    )
  }

  private renderSeparator(): JSX.Element {
    const isSidebarCollapsed = this.isSidebarCollapsed
    return (
      <div
        className={classList({
          'bt-input-border opacity5 w25 ma-x-only-auto navigation-group-header-separator relative': true,
          my10: isSidebarCollapsed,
          'dummy-width': !isSidebarCollapsed,
        })}
      />
    )
  }

  public get isAdmin(): boolean {
    const { userActiveProjectSettings } = this.props.eventsStore.appState

    return userActiveProjectSettings?.isAdmin
  }

  public get isSidebarCollapsed(): boolean {
    const {
      sidebarOnly,
      projectSetUpPageStore: {
        isSettingsSidebarCollapsed,
        isSharedSidebarCollapsed,
      },
    } = this.props

    return sidebarOnly ? isSharedSidebarCollapsed : isSettingsSidebarCollapsed
  }
}
